import { AnimatePresence, motion } from 'motion/react';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { ApiError, api } from '../api/client';
import { PersonIcon } from '../components/AuthIcons';
import { AuthField } from '../components/AuthField';
import { authFieldVariants, useAuthCardMotion } from '../components/AuthSplitShell';
import { useLanguage } from '../lib/i18n/LanguageContext';

function MotionField({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <motion.div className={className} variants={authFieldVariants}>
      {children}
    </motion.div>
  );
}

/**
 * Request a password-reset email. Always treats the API as success on 202 —
 * the response must never reveal whether the address has an account.
 * Shell/copy live in AuthCredentialsLayout (same crossfade as login ↔ signup).
 */
export function ForgotPassword() {
  const { dict } = useLanguage();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const cardMotion = useAuthCardMotion(sent ? 'forgot-sent' : 'forgot-form');

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.requestPasswordReset(email.trim());
      setSent(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setError(dict.forgotPassword.errTooMany);
      } else {
        setError(dict.forgotPassword.errGeneric);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <AnimatePresence mode="wait">
      {sent ? (
        <motion.div key="sent" className="auth-split-form-inner" {...cardMotion}>
          <motion.h1 variants={authFieldVariants}>{dict.forgotPassword.sentTitle}</motion.h1>
          <motion.p className="auth-lede" variants={authFieldVariants}>
            {dict.forgotPassword.sentBody(email.trim())}
          </motion.p>
          <MotionField>
            <Link to="/login" className="btn-block">
              {dict.forgotPassword.backToSignIn}
            </Link>
          </MotionField>
        </motion.div>
      ) : (
        <motion.form
          key="form"
          className="auth-split-form-inner"
          onSubmit={(e) => void submit(e)}
          {...cardMotion}
        >
          <motion.h1 variants={authFieldVariants}>{dict.forgotPassword.title}</motion.h1>
          <motion.p className="auth-lede" variants={authFieldVariants}>
            {dict.forgotPassword.lede}
          </motion.p>

          {error && (
            <MotionField>
              <div className="err">{error}</div>
            </MotionField>
          )}

          <MotionField>
            <AuthField
              id="forgotEmail"
              label={dict.forgotPassword.emailLabel}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
              trailing={<PersonIcon />}
            />
          </MotionField>

          <MotionField>
            <button type="submit" disabled={busy || !email.trim()} className="btn-block">
              {busy ? dict.forgotPassword.sending : dict.forgotPassword.sendLink}
            </button>
          </MotionField>

          <motion.p className="note" variants={authFieldVariants}>
            <Link to="/login">{dict.forgotPassword.backToSignIn}</Link>
          </motion.p>
        </motion.form>
      )}
    </AnimatePresence>
  );
}
